import { useState } from 'react';
import {
  RiArrowRightLine,
  RiGlobalLine,
  RiMailLine,
  RiRadarLine,
  RiText,
  RiDownload2Line,
  RiShieldCheckLine,
  RiAlertLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useStore } from '../../state/store';
import { entityIcon } from '../icons';
import { cn } from '@/lib/utils';

const KINDS = [
  { id: 'website', label: '网页', icon: RiRadarLine, placeholder: 'https://example.com/about' },
  { id: 'domain', label: '域名', icon: RiGlobalLine, placeholder: 'example.com' },
  { id: 'email', label: '邮箱', icon: RiMailLine, placeholder: 'user@example.com' },
  { id: 'text', label: '文本', icon: RiText, placeholder: '粘贴公开文本，自动抽取线索…' },
] as const;

export function CollectPanel() {
  const collect = useStore(state => state.collect);
  const lastBundle = useStore(state => state.lastBundle);
  const activeTargetId = useStore(state => state.activeTargetId);
  const activeEngagementId = useStore(state => state.activeEngagementId);
  const engagements = useStore(state => state.engagements);
  const addEntity = useStore(state => state.addEntity);
  const importIntel = useStore(state => state.importIntel);
  const meta = useStore(state => state.meta);

  const [kind, setKind] = useState<(typeof KINDS)[number]['id']>('website');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [importText, setImportText] = useState('');

  const engagement = engagements.find(e => e.id === activeEngagementId);
  const scope = [
    ...(engagement?.scope.domains ?? []),
    ...(engagement?.scope.ips ?? []),
    ...(engagement?.scope.emails ?? []),
  ];

  const submit = async () => {
    if (!value.trim() || busy) return;
    setBusy(true);
    await collect(kind, value.trim());
    setBusy(false);
  };

  const handleImport = async () => {
    try {
      const parsed = JSON.parse(importText);
      await importIntel({ ...parsed, targetId: activeTargetId });
      setImportText('');
    } catch {
      useStore.getState().toast('error', 'JSON 解析失败，请检查格式');
    }
  };

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="rounded-[var(--radius)] border border-border bg-muted/40 p-2.5">
        <div className="flex items-center gap-1.5 text-[11px] font-medium">
          <RiShieldCheckLine className="size-3.5 text-[var(--color-evidence)]" />
          检索范围（授权限定）
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {scope.length === 0 && <span className="text-[11px] text-muted-foreground">未登记域名/IP/邮箱</span>}
          {scope.map(item => (
            <code key={item} className="rounded border border-border bg-card px-1.5 py-px font-mono text-[10px]">
              {item}
            </code>
          ))}
        </div>
        {!meta?.collectEnabled && (
          <div className="mt-2 flex items-center gap-1 text-[11px] text-amber-700">
            <RiAlertLine className="size-3.5" />
            检索器已被管理员停用
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-1">
        {KINDS.map(item => (
          <button
            key={item.id}
            type="button"
            onClick={() => setKind(item.id)}
            className={cn(
              'flex flex-col items-center gap-1 rounded-[var(--radius-sm)] border py-1.5 text-[10.5px] transition-colors',
              kind === item.id
                ? 'border-primary bg-primary/8 text-primary'
                : 'border-border bg-card text-muted-foreground hover:bg-accent'
            )}
          >
            <item.icon className="size-3.5" />
            {item.label}
          </button>
        ))}
      </div>

      {kind === 'text' ? (
        <Textarea
          rows={4}
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={KINDS.find(k => k.id === kind)?.placeholder}
        />
      ) : (
        <Input
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder={KINDS.find(k => k.id === kind)?.placeholder}
        />
      )}

      <Button size="sm" disabled={busy || !value.trim()} onClick={submit}>
        <RiRadarLine className={cn('size-3.5', busy && 'animate-spin')} />
        {busy ? '检索中…' : '执行检索'}
      </Button>

      {lastBundle && (
        <div className="flex flex-col gap-2 rounded-[var(--radius)] border border-border bg-card p-2.5">
          <div className="flex items-center gap-1.5">
            <Badge variant="default">检索结果</Badge>
            <span className="text-[10.5px] text-muted-foreground">{lastBundle.input.kind}</span>
          </div>
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">{lastBundle.summary}</p>

          {lastBundle.dnsSecurity && (
            <div className="grid grid-cols-2 gap-1.5 rounded-[var(--radius-sm)] bg-muted/60 p-2 text-[10.5px]">
              <span className="text-muted-foreground">SPF</span>
              <span className={lastBundle.dnsSecurity.hasSpf ? 'text-[var(--color-evidence)]' : 'text-[var(--color-cord)]'}>
                {lastBundle.dnsSecurity.hasSpf ? '已配置' : '缺失'}
              </span>
              <span className="text-muted-foreground">邮件记录 MX</span>
              <span>{lastBundle.dnsSecurity.hasMx ? '存在' : '无'}</span>
              {lastBundle.rdap?.registrar && (
                <>
                  <span className="text-muted-foreground">注册商</span>
                  <span className="truncate">{lastBundle.rdap.registrar}</span>
                </>
              )}
            </div>
          )}

          <div className="label-caps">候选线索 · 点击归档</div>
          <div className="flex flex-wrap gap-1">
            {lastBundle.entities.slice(0, 40).map((entity, index) => {
              const Icon = entityIcon(entity.type);
              return (
                <button
                  key={`${entity.type}-${entity.value}-${index}`}
                  type="button"
                  title={`${entity.type} · 置信度 ${(entity.confidence * 100).toFixed(0)}%`}
                  onClick={() =>
                    addEntity({
                      targetId: activeTargetId,
                      type: entity.type,
                      value: entity.value,
                      label: entity.label,
                      confidence: entity.confidence,
                      source: entity.source,
                      metadata: entity.metadata,
                    })
                  }
                  className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 font-mono text-[10px] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  <Icon className="size-3 shrink-0" />
                  <span className="truncate">{entity.value}</span>
                </button>
              );
            })}
            {lastBundle.entities.length === 0 && (
              <span className="text-[11px] text-muted-foreground">未发现候选线索</span>
            )}
          </div>

          <div className="label-caps">留档证据 {lastBundle.evidence.length} 条</div>
          <ul className="flex flex-col gap-0.5">
            {lastBundle.evidence.map(item => (
              <li key={item.hash} className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                <Badge variant="muted">{item.kind}</Badge>
                <span className="truncate">{item.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <details className="rounded-[var(--radius)] border border-border bg-card p-2.5">
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <RiDownload2Line className="size-3.5" />
            批量导入结构化线索
          </span>
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <p className="font-mono text-[9.5px] leading-relaxed text-muted-foreground">
            {'{"entities":[{"type":"domain","value":"a.example"}],"relations":[{"sourceValue":"a.example","targetValue":"b.example","type":"owns"}]}'}
          </p>
          <Textarea
            rows={4}
            className="font-mono text-[10.5px]"
            value={importText}
            onChange={e => setImportText(e.target.value)}
            placeholder='{"entities":[...]}'
          />
          <Button size="sm" variant="outline" onClick={handleImport}>
            <RiArrowRightLine className="size-3.5" />
            导入
          </Button>
        </div>
      </details>
    </div>
  );
}